import type {
  ListUnresolvedVendorErrorCodesResponseDto,
  UnresolvedVendorErrorCodeRemarkActionRequestDto,
  UnresolvedVendorErrorCodeResponseDto,
  UpdateUnresolvedVendorErrorCodeRequestDto,
} from '@evimo/client/models';
import { ResponseError } from '@evimo/client/runtime';
import { UnresolvedVendorErrorCodeApiError } from '~/app/services/errorManagement/unresolved-vendor-error-code-api-error';
import {
  unresolvedVendorErrorCodesApi,
  unresolvedVendorErrorCodesRequestInit,
} from '~/app/services/errorManagement/unresolved-vendor-error-code-api-instances';
import type { UnresolvedVendorErrorCodeStatus } from '@evimo/types/enums/unresolved-vendor-error-code-status';

export { UnresolvedVendorErrorCodeApiError };

async function invokeUnresolvedClient<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof ResponseError) {
      throw new UnresolvedVendorErrorCodeApiError(
        error.response.status,
        error.message,
      );
    }
    throw error;
  }
}

export type ListUnresolvedVendorErrorCodesParams = {
  page: number;
  limit: number;
  q?: string;
  manufacturerId?: string;
  status?: UnresolvedVendorErrorCodeStatus;
  sortOrder?: 'asc' | 'desc';
};

export const UnresolvedVendorErrorCodeService = {
  list(
    params: ListUnresolvedVendorErrorCodesParams,
  ): Promise<ListUnresolvedVendorErrorCodesResponseDto> {
    return invokeUnresolvedClient(() =>
      unresolvedVendorErrorCodesApi.unresolvedVendorErrorCodeControllerFindAll(
        {
          page: params.page,
          limit: params.limit,
          ...(params.q?.trim() ? { q: params.q.trim() } : {}),
          ...(params.manufacturerId
            ? { manufacturerId: params.manufacturerId }
            : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
        unresolvedVendorErrorCodesRequestInit,
      ),
    );
  },

  getById(id: string): Promise<UnresolvedVendorErrorCodeResponseDto> {
    return invokeUnresolvedClient(() =>
      unresolvedVendorErrorCodesApi.unresolvedVendorErrorCodeControllerFindById(
        { id },
        unresolvedVendorErrorCodesRequestInit,
      ),
    );
  },

  update(
    id: string,
    dto: UpdateUnresolvedVendorErrorCodeRequestDto,
  ): Promise<UnresolvedVendorErrorCodeResponseDto> {
    return invokeUnresolvedClient(() =>
      unresolvedVendorErrorCodesApi.unresolvedVendorErrorCodeControllerUpdate(
        { id, updateUnresolvedVendorErrorCodeRequestDto: dto },
        unresolvedVendorErrorCodesRequestInit,
      ),
    );
  },

  resolve(
    id: string,
    dto: UnresolvedVendorErrorCodeRemarkActionRequestDto,
  ): Promise<UnresolvedVendorErrorCodeResponseDto> {
    return invokeUnresolvedClient(() =>
      unresolvedVendorErrorCodesApi.unresolvedVendorErrorCodeControllerResolve(
        { id, unresolvedVendorErrorCodeRemarkActionRequestDto: dto },
        unresolvedVendorErrorCodesRequestInit,
      ),
    );
  },

  ignore(
    id: string,
    dto: UnresolvedVendorErrorCodeRemarkActionRequestDto,
  ): Promise<UnresolvedVendorErrorCodeResponseDto> {
    return invokeUnresolvedClient(() =>
      unresolvedVendorErrorCodesApi.unresolvedVendorErrorCodeControllerIgnore(
        { id, unresolvedVendorErrorCodeRemarkActionRequestDto: dto },
        unresolvedVendorErrorCodesRequestInit,
      ),
    );
  },

  unignore(
    id: string,
    dto: UnresolvedVendorErrorCodeRemarkActionRequestDto,
  ): Promise<UnresolvedVendorErrorCodeResponseDto> {
    return invokeUnresolvedClient(() =>
      unresolvedVendorErrorCodesApi.unresolvedVendorErrorCodeControllerUnignore(
        { id, unresolvedVendorErrorCodeRemarkActionRequestDto: dto },
        unresolvedVendorErrorCodesRequestInit,
      ),
    );
  },
};
